import {format, parse} from 'date-fns';
import {fromZonedTime, toZonedTime} from 'date-fns-tz';
import {useDatePatterns} from './useDatePatterns';
import type {MaltDatePickerView} from '../MaltDatePicker/MaltDatePicker.vue';

export function useDateFormatters() {
    const {dateUnicodePattern, dateMonthYearUnicodePattern, dateTimeUnicodePattern} = useDatePatterns();

    /** Returns a Date to a Unicode dd/MM/YYYY format */
    function formatZoneDateToString(date: Date, view: MaltDatePickerView): string {
        try {
            if (view === 'month') {
                return format(toZonedTime(date, 'UTC'), dateMonthYearUnicodePattern);
            } else if (view === 'year') {
                return format(toZonedTime(date, 'UTC'), 'yyyy');
            } else if (view === 'date-time') {
                return format(toZonedTime(date, 'UTC'), dateTimeUnicodePattern);
            }
            return format(toZonedTime(date, 'UTC'), dateUnicodePattern);
        } catch {
            return dateUnicodePattern;
        }
    }

    /** Converts the Date to a Timezone-aware Date to display in the datepicker */
    function dateToZonedTime(date: Date): Date | undefined {
        return !isNaN(date.valueOf()) ? toZonedTime(date, 'UTC') : undefined;
    }

    function parseValueFromTimezone(value: string | Date | undefined | null, view: MaltDatePickerView): Date | undefined {
        if (!value) {
            return undefined;
        }
        if (value instanceof Date) {
            /** As any because of Vue TS generic problem */
            return fromZonedTime(value, 'UTC');
        } else {
            try {
                if (view === 'date') {
                    return fromZonedTime(parse(value as string, dateUnicodePattern, new Date()), 'UTC');
                } else if (view === 'month') {
                    return fromZonedTime(parse(value as string, dateMonthYearUnicodePattern, new Date()), 'UTC');
                } else if (view === 'year') {
                    return fromZonedTime(parse(value as string, 'yyyy', new Date()), 'UTC');
                } else if (view === 'date-time') {
                    return fromZonedTime(parse(value as string, dateTimeUnicodePattern, new Date()), 'UTC');
                }
            } catch {
                return undefined;
            }
            return undefined;
        }
    }

    return {
        formatZoneDateToString,
        dateToZonedTime,
        parseValueFromTimezone,
    };
}
